import PropTypes from "prop-types";
import { useMemo, useState } from "react";
import { getInitials } from "../../../util/text";
import SearchInput from "./SearchInput.jsx";
import s from "./TalentPanel.module.css";
import { Badge, Typography } from "../../../themeV2";

export default function TalentPanel({ vaList, activeVa, onVaSelect }) {
  const [vaSearch, setVaSearch] = useState("");

  const filtered = useMemo(() => {
    if (!vaSearch.trim()) return vaList;
    const q = vaSearch.toLowerCase().trim();
    return vaList.filter(
      (va) =>
        va.name.toLowerCase().includes(q) ||
        va.chars.some((c) => c.toLowerCase().includes(q)),
    );
  }, [vaList, vaSearch]);

  const grouped = useMemo(
    () =>
      Object.entries(
        filtered.reduce((acc, va) => {
          const key = va.name?.charAt?.(0).toUpperCase();
          if (!key) return acc;
          acc[key] ??= [];
          acc[key].push(va);
          return acc;
        }, {}),
      )
        .map(([letter, items]) => [
          letter,
          items.sort((a, b) => a.name.localeCompare(b.name)),
        ])
        .sort(([a], [b]) => a.localeCompare(b)),
    [filtered],
  );

  return (
    <aside className={s.panel} aria-label="Browse by talent">
      <SearchInput
        className={s.panelSearchField}
        placeholder="Filter talent…"
        ariaLabel="Filter talent"
        value={vaSearch}
        onChange={(e) => setVaSearch(e.target.value)}
      />
      {activeVa && (
        <button className={s.clearFilter} onClick={() => onVaSelect(null)}>
          Filtering by {activeVa} — clear
        </button>
      )}
      {grouped.length > 0 ? (
        <div className={s.alphaGroups}>
          {grouped.map(([letter, vas]) => (
            <div key={letter}>
              <Typography
                variant="label-lg"
                color="variant"
                weight="bold"
                as="div"
                className={s.alphaLetter}
              >
                {letter}
              </Typography>
              <div className={s.alphaList}>
                {vas.map((va) => (
                  <button
                    key={va.name}
                    type="button"
                    className={activeVa === va.name ? s.vaRowSelected : s.vaRow}
                    onClick={() =>
                      onVaSelect(activeVa === va.name ? null : va.name)
                    }
                    aria-pressed={activeVa === va.name}
                  >
                    {va.avatarUrl ? (
                      <img
                        className={s.vaAvatar}
                        src={va.avatarUrl}
                        alt={va.name}
                      />
                    ) : (
                      <div className={s.vaInitials}>{getInitials(va.name)}</div>
                    )}
                    <div className={s.vaRowInfo}>
                      <span className={s.vaRowName}>{va.name}</span>
                      <span className={s.vaRowDetail}>
                        {va.chars.slice(0, 3).join(", ")}
                        {va.chars.length > 3 && ` +${va.chars.length - 3}`}
                      </span>
                    </div>
                    <Badge
                      variant="count"
                      tone="neutral"
                      count={va.count}
                      className={s.vaRowCount}
                    />
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className={s.noResults}>
          No talent match &ldquo;{vaSearch}&rdquo;
        </div>
      )}
    </aside>
  );
}

TalentPanel.propTypes = {
  vaList: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      chars: PropTypes.arrayOf(PropTypes.string).isRequired,
      count: PropTypes.number,
      avatarUrl: PropTypes.string,
    }),
  ).isRequired,
  activeVa: PropTypes.string,
  onVaSelect: PropTypes.func.isRequired,
};
